import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { DollarSign, Link as LinkIcon, BarChart3, Calendar, ArrowRight, Copy, Check, Twitter, Facebook, Mail, MessageCircle, Sparkles, ShieldCheck, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: DollarSign, title: '15% Commission', desc: 'Earn 15% on every sale made through your referral link.' },
  { icon: LinkIcon, title: '90-Day Tracking', desc: 'Get credited even if buyers return up to 90 days later.' },
  { icon: BarChart3, title: 'Real-time Stats', desc: 'Track clicks, sales and earnings live on your dashboard.' },
  { icon: Calendar, title: 'Monthly Payouts', desc: 'Receive your commissions via PayPal once you hit $100.' },
];

const STEPS = [
  { num: 1, title: 'Sign Up', desc: 'Create your free 1on1trade account, then join the affiliate program.' },
  { num: 2, title: 'Share', desc: 'Copy your unique link and share it anywhere — Twitter, YouTube, forums, friends.' },
  { num: 3, title: 'Earn', desc: 'Get 15% of every sale. Request payout once you reach $100.' },
];

const MARKETING_KIT = [
  {
    title: 'Twitter / X',
    text: "If you're learning forex and tired of YouTube fluff, check out Hettie Nel's 9-Step System — 20+ years of real trading distilled into a structured course. Started with the beginner one ($97), made my money back in the first month.",
  },
  {
    title: 'Trading group / WhatsApp',
    text: "Just enrolled in this forex course called Forex Made Simple by Hettie Nel at 1on1trade — actually starts from zero and builds you up properly. Recommend it if you're still figuring things out.",
  },
  {
    title: 'YouTube description',
    text: "The course I'm using → [YOUR LINK]\nForex Made Simple by Hettie Nel ($97) — covers everything from candlesticks to your first live trade. 20+ years of real trading experience packaged into 16 short lessons.",
  },
];

function StatCard({ label, value, accent = 'amber' }) {
  const colors = {
    amber: 'text-amber-400',
    emerald: 'text-emerald-400',
    sky: 'text-sky-400',
    violet: 'text-violet-400',
  };
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${colors[accent]} tabular-nums`}>{value}</p>
    </div>
  );
}

function CopyButton({ value, testid, label = 'Copy', successMsg = 'Copied to clipboard' }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(successMsg, { description: value.length > 60 ? value.slice(0, 60) + '…' : value, duration: 2200 });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — please try again');
    }
  };
  return (
    <button onClick={onCopy} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${copied ? 'bg-emerald-400 text-black' : 'bg-amber-400 text-black hover:bg-amber-300'}`} data-testid={testid}>
      {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> {label}</>}
    </button>
  );
}

function AffiliateDashboard({ dashboard, onPayoutRequested }) {
  const refLink = `${window.location.origin}/?ref=${dashboard.code}`;
  const [paypalEmail, setPaypalEmail] = useState(dashboard.paypal_email || '');
  const [requesting, setRequesting] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState('');

  const pending = dashboard.stats.pending_payout;
  const canPayout = pending >= dashboard.config.min_payout_usd;

  const requestPayout = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      setPayoutMsg('Please enter a valid PayPal email.');
      return;
    }
    setRequesting(true);
    setPayoutMsg('');
    try {
      const { data } = await axios.post(`${API}/api/affiliate/payout-request`, { paypal_email: paypalEmail }, { withCredentials: true });
      setPayoutMsg(`Payout request for $${data.amount} sent. We'll process within 5 business days.`);
      onPayoutRequested?.();
    } catch (err) {
      setPayoutMsg(err.response?.data?.detail || 'Failed to request payout.');
    } finally {
      setRequesting(false);
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent('Learning forex the right way → ' + refLink)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refLink)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent('Check this out: ' + refLink)}`,
    email: `mailto:?subject=${encodeURIComponent('Forex training I recommend')}&body=${encodeURIComponent("Thought you'd find this useful: " + refLink)}`,
  };

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      {/* Referral link */}
      <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.03] p-6 sm:p-7" data-testid="referral-link-card">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-400 mb-3">Your Referral Link</p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 rounded-lg bg-[#050814] border border-white/10 px-4 py-3 text-sm text-white/85 font-mono break-all" data-testid="referral-link-text">
            {refLink}
          </div>
          <CopyButton value={refLink} testid="copy-referral-link" />
        </div>

        {/* Share buttons */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-white/40 mr-1">Share to:</span>
          <a href={shareLinks.twitter} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white/80 transition-colors" data-testid="share-twitter">
            <Twitter size={13} /> X / Twitter
          </a>
          <a href={shareLinks.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white/80 transition-colors" data-testid="share-facebook">
            <Facebook size={13} /> Facebook
          </a>
          <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white/80 transition-colors" data-testid="share-whatsapp">
            <MessageCircle size={13} /> WhatsApp
          </a>
          <a href={shareLinks.email} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white/80 transition-colors" data-testid="share-email">
            <Mail size={13} /> Email
          </a>
        </div>
        <p className="mt-4 text-[11px] text-white/40">
          Code: <span className="font-mono text-amber-400">{dashboard.code}</span> · {Math.round(dashboard.config.commission_rate * 100)}% commission · {dashboard.config.attribution_days}-day attribution
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Clicks" value={dashboard.stats.clicks} accent="sky" />
        <StatCard label="Sales" value={dashboard.stats.sales} accent="violet" />
        <StatCard label="Total Earned" value={`$${dashboard.stats.total_earned.toFixed(2)}`} accent="amber" />
        <StatCard label="Pending Payout" value={`$${pending.toFixed(2)}`} accent="emerald" />
      </div>

      {/* Payout section */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7" data-testid="payout-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Request Payout</h3>
            <p className="text-xs text-white/40 mt-1">Minimum: ${dashboard.config.min_payout_usd} · Via PayPal · Paid out within 5 business days</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/40">Available</p>
            <p className="text-2xl font-bold text-emerald-400">${pending.toFixed(2)}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            placeholder="Your PayPal email"
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            className="flex-1 rounded-lg bg-[#050814] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
            data-testid="paypal-email-input"
          />
          <button
            onClick={requestPayout}
            disabled={!canPayout || requesting}
            className="px-6 py-3 rounded-lg bg-amber-400 text-black text-sm font-bold hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="request-payout-btn"
          >
            {requesting ? 'Requesting…' : canPayout ? 'Request Payout' : `Need $${(dashboard.config.min_payout_usd - pending).toFixed(2)} more`}
          </button>
        </div>
        {payoutMsg && <p className="mt-3 text-xs text-amber-400" data-testid="payout-msg">{payoutMsg}</p>}
      </div>

      {/* Marketing kit */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7" data-testid="marketing-kit">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-amber-400" />
          <h3 className="text-lg font-bold text-white">Ready-to-use Marketing Kit</h3>
        </div>
        <p className="text-xs text-white/40 mb-5">Pre-written posts you can copy, customize, and share. Replace [YOUR LINK] with your referral link.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {MARKETING_KIT.map((m) => (
            <div key={m.title} className="rounded-xl border border-white/[0.06] bg-[#050814] p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400">{m.title}</p>
                <CopyButton value={m.text.replace('[YOUR LINK]', refLink)} testid={`copy-${m.title.toLowerCase().replace(/[^a-z]/g, '-')}`} />
              </div>
              <p className="text-xs text-white/60 leading-relaxed whitespace-pre-line">{m.text.replace('[YOUR LINK]', refLink)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent commissions */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7" data-testid="recent-commissions">
        <h3 className="text-lg font-bold text-white mb-4">Recent Sales</h3>
        {dashboard.recent_commissions.length === 0 ? (
          <p className="text-sm text-white/40">No sales yet — start sharing your link!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/[0.06]">
                  <th className="pb-3 font-semibold">Date</th>
                  <th className="pb-3 font-semibold">Product</th>
                  <th className="pb-3 font-semibold text-right">Order</th>
                  <th className="pb-3 font-semibold text-right">Your Cut</th>
                  <th className="pb-3 font-semibold text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {dashboard.recent_commissions.map((c, i) => (
                  <tr key={i} className="text-sm">
                    <td className="py-3 text-white/50 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="py-3 text-white/80 text-xs">{c.product_names}</td>
                    <td className="py-3 text-white/60 text-right tabular-nums">${c.order_amount.toFixed(2)}</td>
                    <td className="py-3 text-emerald-400 text-right tabular-nums font-semibold">${c.commission_amount.toFixed(2)}</td>
                    <td className="py-3 text-right">
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${c.status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>{c.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AffiliatePage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [notAffiliate, setNotAffiliate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);

  const loadDashboard = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/affiliate/dashboard`, { withCredentials: true });
      setDashboard(data);
      setNotAffiliate(false);
    } catch (err) {
      if (err.response?.status === 404) setNotAffiliate(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async () => {
    if (!user) { window.location.href = '/register'; return; }
    setJoining(true);
    try {
      await axios.post(`${API}/api/affiliate/apply`, {}, { withCredentials: true });
      await loadDashboard();
    } catch (err) {
      alert('Failed to join. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="affiliate-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-amber-400/[0.04] blur-[140px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400/80 mb-3">Affiliate Program</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
            Earn <span className="text-amber-400">15%</span> on every sale
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/55 max-w-2xl mx-auto leading-relaxed">
            Share Hettie&apos;s courses with your trading community and get paid every time someone learns from her. No caps. No quotas. Just real commissions, paid out monthly via PayPal.
          </p>
        </div>
      </section>

      {/* If user already an affiliate → show dashboard */}
      {dashboard && (
        <section className="px-4 sm:px-6 lg:px-8" data-testid="dashboard-section">
          <div className="mx-auto max-w-5xl">
            <AffiliateDashboard dashboard={dashboard} onPayoutRequested={loadDashboard} />
          </div>
        </section>
      )}

      {/* Else → show join section */}
      {!dashboard && (
        <>
          {/* Features */}
          <section className="px-4 sm:px-6 lg:px-8 pb-12" data-testid="affiliate-features">
            <div className="mx-auto max-w-5xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-amber-400/20 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-amber-400/10 flex items-center justify-center mb-4">
                      <Icon size={18} className="text-amber-400" />
                    </div>
                    <h3 className="text-sm font-bold text-white">{f.title}</h3>
                    <p className="mt-1.5 text-xs text-white/50 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Earnings example */}
          <section className="px-4 sm:px-6 lg:px-8 pb-12" data-testid="earnings-example">
            <div className="mx-auto max-w-3xl rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.03] p-7 sm:p-9">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-3">Quick example</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Refer 5 friends → earn $400+</h2>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">
                One sale of the 9-Step Pro System ($497) = <span className="text-emerald-400 font-bold">$74.55 in your pocket</span>. One Pro Trader Bundle sale ($897) = <span className="text-emerald-400 font-bold">$134.55</span>. Refer 5 buyers across the catalog and you can comfortably clear <span className="text-emerald-400 font-bold">$400+</span> per month.
              </p>
            </div>
          </section>

          {/* How it works */}
          <section className="px-4 sm:px-6 lg:px-8 pb-12 border-t border-white/[0.04] pt-12" data-testid="how-it-works">
            <div className="mx-auto max-w-4xl">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center tracking-tight">How it works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {STEPS.map(s => (
                  <div key={s.num} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-400 text-black text-lg font-black mb-4">{s.num}</span>
                    <h3 className="font-bold text-white text-base">{s.title}</h3>
                    <p className="text-xs text-white/50 mt-2 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Trust strip */}
          <section className="px-4 sm:px-6 lg:px-8 pb-12" data-testid="trust-strip">
            <div className="mx-auto max-w-3xl flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-white/50">
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400" /> $100 minimum payout</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400" /> 90-day attribution window</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400" /> PayPal payouts worldwide</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400" /> Free to join, no quotas</span>
            </div>
          </section>

          {/* CTA */}
          <section className="px-4 sm:px-6 lg:px-8 text-center" data-testid="affiliate-cta">
            <div className="mx-auto max-w-2xl">
              <button
                onClick={handleJoin}
                disabled={joining || loading}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-amber-400 text-black font-bold text-base hover:bg-amber-300 transition-all hover:scale-[1.02] disabled:opacity-50"
                data-testid="join-affiliate-btn"
              >
                {joining ? 'Joining…' : user ? (notAffiliate ? 'Join the Affiliate Program' : 'Apply Now') : 'Create Account to Join'}
                <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-xs text-white/40">{user ? 'Free to join · Instant approval' : 'Free account · Takes 30 seconds'}</p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
